import type { SeoResolver } from '../types';

// TNEB source data stores region/circle names in inconsistent casing
// (e.g. "madurai", "MADURAI"); normalise for titles and structured data.
const titleCase = (s?: string | number | null): string | undefined => {
  if (s === undefined || s === null || s === '') return undefined;
  return String(s).replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
};

/**
 * SEO for a resolved TNEB section office. Moved verbatim from the inline
 * branches in SEO.tsx so the rendered title/description/schema are
 * byte-identical to the pre-framework output.
 */
export const tnebSeoResolver: SeoResolver = (ctx) => {
  const j = ctx.selections.tnebSection;
  if (!j) return null;

  const { siteName, layerName, pageUrl } = ctx;

  const office = j.returned_office || j.section_na || 'TNEB Office';
  const sec = j.section_co ? ` (Section ${j.section_co})` : '';
  const hint = (j.mobile || j.email) ? ' — Phone & Email' : '';
  const whereTitle = titleCase(j.district || j.region_nam || j.circle_nam);
  const title = `${office}${sec}${hint} | ${layerName}${whereTitle ? ` in ${whereTitle}` : ''} | ${siteName}`;

  const officeDesc = j.returned_office || j.section_na || 'TNEB Section';
  const whereDesc = titleCase(j.district || j.region_nam || j.circle_nam) || 'Tamil Nadu';
  const contact: string[] = [];
  if (j.mobile) contact.push(`Phone: ${j.mobile}`);
  if (j.email) contact.push(`Email: ${j.email}`);
  const contactStr = contact.length ? ` ${contact.join('. ')}.` : '';
  const pinStr = j.pincode ? ` for pincode ${j.pincode}` : '';
  const description = `${officeDesc}${sec} — Tamil Nadu Electricity Board (TNEB / TANGEDCO) section office in ${whereDesc}${pinStr}.${contactStr} View office location, jurisdiction boundary, and directions on ${siteName}.`;

  const coords = j.office_location as number[] | undefined;
  const schemaName = j.returned_office || j.section_na || 'TNEB Section Office';
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'GovernmentOffice',
    name: `${schemaName}${j.section_co ? ` (Section ${j.section_co})` : ''}`,
    url: pageUrl,
    parentOrganization: {
      '@type': 'GovernmentOrganization',
      name: 'Tamil Nadu Generation and Distribution Corporation (TANGEDCO)',
      alternateName: 'TNEB'
    },
    address: {
      '@type': 'PostalAddress',
      ...(titleCase(j.district || j.region_nam) ? { addressLocality: titleCase(j.district || j.region_nam) } : {}),
      ...(j.pincode ? { postalCode: String(j.pincode) } : {}),
      addressRegion: 'Tamil Nadu',
      addressCountry: 'IN'
    }
  };
  if (j.section_co) schema.branchCode = String(j.section_co);
  if (j.mobile) schema.telephone = `+91${j.mobile}`;
  if (j.email) schema.email = j.email;
  if (j.subdivisio || j.division_n) {
    schema.areaServed = { '@type': 'AdministrativeArea', name: titleCase(j.subdivisio || j.division_n) };
  }
  if (coords && coords.length >= 2) {
    schema.geo = { '@type': 'GeoCoordinates', latitude: coords[1], longitude: coords[0] };
  }

  return {
    title,
    description,
    breadcrumbLabel: j.returned_office || j.section_na || '',
    schema
  };
};
